import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import PageWrapper from '../PageWrapper/PageWrapper';
import { AdminRestricted } from '../../components/AdminRestricted/AdminRestricted';
import { CardV2, CardHeader, CardTitle, CardContent } from '../../components/CardV2/CardV2';
import { TabV2, TabsContent, TabsList, TabsTrigger } from '../../components/TabV2/TabV2';
import { Users, AlertTriangle, ClipboardList, BarChart3, TrendingUp, DollarSign, Package } from 'lucide-react';
import SectionTitle from '../../components/SectionTitle/SectionTitle';
import BarChart from '../../components/BarChart/BarChart';
import PieChart from '../../components/PieChart/PieChart';
import NoData from '../../components/NoData/NoData';

import MeetingsTotalOverTime from '../MeetingsStatisticsPage/graphs/MeetingsTotalOverTime';
import MeetingsTypeDistribution from '../MeetingsStatisticsPage/graphs/MeetingsTypeDistribution';
import NoShowsRatioCard from '../MeetingsStatisticsPage/graphs/NoShowsRatioCard';
import NoShowsOverTime from '../MeetingsStatisticsPage/graphs/NoShowsOverTime';
import BookingsTotalPendingCard from '../MeetingsStatisticsPage/graphs/BookingsTotalPendingCard';
import BookingsTypeDistribution from '../MeetingsStatisticsPage/graphs/BookingsTypeDistribution';
import BookingsCancellationRatioCard from '../MeetingsStatisticsPage/graphs/BookingsCancellationRatioCard';
import BookingsInboundChannels from '../MeetingsStatisticsPage/graphs/BookingsInboundChannels';
import BookingsPendingPriceRanges from '../MeetingsStatisticsPage/graphs/BookingsPendingPriceRanges';
import UpcomingVisitsCard from '../MeetingsStatisticsPage/graphs/UpcomingVisitsCard';
import ConversionRateCard from '../MeetingsStatisticsPage/graphs/ConversionRateCard';
import AverageSalePriceCard from '../MeetingsStatisticsPage/graphs/AverageSalePriceCard';
import AverageDressSalePriceCard from '../MeetingsStatisticsPage/graphs/AverageDressSalePriceCard';
import DepositedPaymentsCard from '../MeetingsStatisticsPage/graphs/DepositedPaymentsCard';
import PendingPaymentsCard from '../MeetingsStatisticsPage/graphs/PendingPaymentsCard';

import CustomersCrmStatisticsAPI from '../../api/CustomersCrmStatisticsAPI';
import CustomersCrmTotalCard from '../CustomersCrmStatisticsPage/components/CustomersCrmTotalCard';
import { inboundChannelToTranslationKeys } from '../../shared/inboundChannelToTranslationKey';

import SalesInsightsAPI, { MoneyInResponse, SalesRepresentativeMoneyResponse, SalesRepresentativeCountResponse, RankingOfSoldItemsResponse } from '../../api/SalesInsightsAPI';

type TimeRange = { start: string; end: string };

const toISODate = (d: Date) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString();
};

const MoneyInPerPeriod = ({ timeRange }: { timeRange: TimeRange }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'daily' | 'monthly'>('daily');
  const [data, setData] = useState<MoneyInResponse>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = mode === 'daily'
          ? await SalesInsightsAPI.getMoneyInPerDay({
              startTime: timeRange.start,
              endTime: timeRange.end,
            })
          : await SalesInsightsAPI.getMoneyInPerMonth({
              startTime: timeRange.start,
              endTime: timeRange.end,
            });
        setData(response.data);
      } catch (error) {
        console.error(`Error fetching money in per ${mode}:`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [timeRange.start, timeRange.end, mode]);

  const labels = useMemo(() => Object.keys(data).sort(), [data]);

  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];

    const sortedLabels = Object.keys(data).sort();
    const amounts = sortedLabels.map(label => data[label] || 0);

    return [
      {
        label: t('sales.insights.money_in') || 'Money In',
        data: amounts,
        backgroundColor: Array(amounts.length).fill('rgba(34, 197, 94, 0.6)'),
        borderColor: Array(amounts.length).fill('rgba(34, 197, 94, 0.8)'),
        borderWidth: 1,
      },
    ];
  }, [data, t]);

  const title = mode === 'daily'
    ? t('sales.insights.money_in_per_day') || 'Money In Per Day'
    : t('sales.insights.money_in_per_month') || 'Money In Per Month';

  if (loading) {
    return (
      <CardV2>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {title}
            </CardTitle>
            <TabV2 value={mode} onValueChange={(v) => setMode(v as 'daily' | 'monthly')}>
              <TabsList className="h-8">
                <TabsTrigger value="daily" className="text-xs px-3 py-1">
                  {t('global.daily') || 'Daily'}
                </TabsTrigger>
                <TabsTrigger value="monthly" className="text-xs px-3 py-1">
                  {t('global.monthly') || 'Monthly'}
                </TabsTrigger>
              </TabsList>
            </TabV2>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </CardV2>
    );
  }

  return (
    <CardV2>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            {title}
          </CardTitle>
          <TabV2 value={mode} onValueChange={(v) => setMode(v as 'daily' | 'monthly')}>
            <TabsList className="h-8">
              <TabsTrigger value="daily" className="text-xs px-3 py-1">
                {t('global.daily') || 'Daily'}
              </TabsTrigger>
              <TabsTrigger value="monthly" className="text-xs px-3 py-1">
                {t('global.monthly') || 'Monthly'}
              </TabsTrigger>
            </TabsList>
          </TabV2>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <BarChart 
            title={title} 
            xLabels={labels} 
            dataSets={chartData} 
          />
        ) : (
          <NoData message={t("global.empty_results")} />
        )}
      </CardContent>
    </CardV2>
  );
};

const MoneyPerSalesRepresentative = ({ timeRange }: { timeRange: TimeRange }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<SalesRepresentativeMoneyResponse>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await SalesInsightsAPI.getMoneyPerSalesRepresentative({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching money per sales representative:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [timeRange.start, timeRange.end]);

  const employeeIds = useMemo(() => {
    return Object.keys(data).sort((a, b) => data[b] - data[a]);
  }, [data]);

  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];

    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const amounts = entries.map(([, amount]) => amount);

    return [{
      label: t('sales.insights.money') || 'Money',
      data: amounts,
      backgroundColor: Array(amounts.length).fill('rgba(59, 130, 246, 0.6)'),
      borderColor: Array(amounts.length).fill('rgba(59, 130, 246, 0.8)'),
      borderWidth: 1,
    }];
  }, [data, t]);

  if (loading) {
    return (
      <CardV2>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('sales.insights.money_per_sales_representative') || 'Money Per Sales Representative'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </CardV2>
    );
  }

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          {t('sales.insights.money_per_sales_representative') || 'Money Per Sales Representative'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <BarChart 
            title={t('sales.insights.money_per_sales_representative') || 'Money Per Sales Representative'} 
            xLabels={employeeIds} 
            dataSets={chartData} 
          />
        ) : (
          <NoData message={t("global.empty_results")} />
        )}
      </CardContent>
    </CardV2>
  );
};

const SalesPerSalesRepresentative = ({ timeRange }: { timeRange: TimeRange }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<SalesRepresentativeCountResponse>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await SalesInsightsAPI.getSalesPerSalesRepresentative({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching sales per sales representative:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [timeRange.start, timeRange.end]);

  const employeeIds = useMemo(() => {
    return Object.keys(data).sort((a, b) => data[b] - data[a]);
  }, [data]);

  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];

    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const counts = entries.map(([, count]) => count);

    return [{
      label: t('sales.insights.sales_count') || 'Sales Count',
      data: counts,
      backgroundColor: Array(counts.length).fill('rgba(16, 185, 129, 0.6)'),
      borderColor: Array(counts.length).fill('rgba(16, 185, 129, 0.8)'),
      borderWidth: 1,
    }];
  }, [data, t]);

  if (loading) {
    return (
      <CardV2>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('sales.insights.sales_per_sales_representative') || 'Sales Per Sales Representative'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </CardV2>
    );
  }

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          {t('sales.insights.sales_per_sales_representative') || 'Sales Per Sales Representative'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <BarChart 
            title={t('sales.insights.sales_per_sales_representative') || 'Sales Per Sales Representative'} 
            xLabels={employeeIds} 
            dataSets={chartData} 
          />
        ) : (
          <NoData message={t("global.empty_results")} />
        )}
      </CardContent>
    </CardV2>
  );
};

const RankingOfSoldItems = ({ timeRange }: { timeRange: TimeRange }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<RankingOfSoldItemsResponse>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await SalesInsightsAPI.getRankingOfSoldItems({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching ranking of sold items:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [timeRange.start, timeRange.end]);

  const tableData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];

    return Object.entries(data)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .map((item, index) => ({
        ...item,
        rank: index + 1
      }));
  }, [data]);


  if (loading) {
    return (
      <CardV2>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t('sales.insights.ranking_of_sold_items') || 'Ranking of Sold Items'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </CardV2>
    );
  }

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          {t('sales.insights.ranking_of_sold_items') || 'Ranking of Sold Items'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-96 overflow-y-auto overflow-x-auto">
          {tableData.length > 0 ? (
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-background z-10">
                <tr className="border-b">
                  <th className="text-left p-3 font-medium text-muted-foreground">
                    {t('sales.insights.rank') || 'Rank'}
                  </th>
                  <th className="text-left p-3 font-medium text-muted-foreground">
                    {t('sales.insights.item_name') || 'Item Name'}
                  </th>
                  <th className="text-right p-3 font-medium text-muted-foreground">
                    {t('sales.insights.total_sold') || 'Total Sold'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((item) => (
                  <tr key={item.name} className="border-b hover:bg-muted/50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-muted-foreground">
                          #{item.rank}
                        </span>
                        {item.rank <= 3 && (
                          <span className={`text-lg ${
                            item.rank === 1 ? 'text-yellow-500' : 
                            item.rank === 2 ? 'text-gray-400' : 
                            'text-amber-600'
                          }`}>
                            {item.rank === 1 ? '🥇' : item.rank === 2 ? '🥈' : '🥉'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="font-medium text-foreground">
                        {item.name}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-lg font-bold text-foreground">
                        {item.count.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <NoData message={t("global.empty_results")} />
          )}
        </div>
      </CardContent>
    </CardV2>
  );
};

// Bookings & Meetings Content Component
const BookingsMeetingsContent = ({ timeRange }: { timeRange: TimeRange }) => {
  const { t } = useTranslation();
  const [nestedTab, setNestedTab] = useState<'meetings' | 'bookings'>('meetings');

  return (
    <TabV2 value={nestedTab} onValueChange={(v) => setNestedTab(v as any)} className="space-y-4">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="meetings" className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          {t('stats.meetings') || 'Meetings'}
        </TabsTrigger>
        <TabsTrigger value="bookings" className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4" />
          {t('stats.bookings') || 'Bookings'}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="meetings" className="space-y-6">
        <SectionTitle text={t('stats.meetings') || 'Meetings'} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <CardV2>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('stats.upcoming_visits') || 'Upcoming visits'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UpcomingVisitsCard timeRange={timeRange} />
            </CardContent>
          </CardV2>
          <CardV2>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('stats.conversion_rate') || 'Conversion rate'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ConversionRateCard timeRange={timeRange} />
            </CardContent>
          </CardV2>
          <CardV2>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                {t('stats.bookings_cancellation_ratio') || 'Cancelled vs Total'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BookingsCancellationRatioCard timeRange={timeRange} />
            </CardContent>
          </CardV2>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {t('stats.meetings_total_over_time') || 'Total Meetings Over Time'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MeetingsTotalOverTime timeRange={timeRange} />
              </CardContent>
            </CardV2>
          </div>
          <div className="lg:col-span-2 space-y-6">
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {t('stats.meetings_type_distribution') || 'Meetings Type Distribution'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MeetingsTypeDistribution timeRange={timeRange} />
              </CardContent>
            </CardV2>
          </div>
        </div>

        <SectionTitle text={t('stats.meetings_no_shows') || 'No shows'} cssStyles="mt-6" />
        <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border">
          <p className="text-sm text-muted-foreground">
            {t('stats.meetings_no_shows_note') || 'To start tracking no-shows, mark meetings as no-shows in the calendar using the icon in the top right corner of each meeting.'}
          </p>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  {t('stats.meetings_noshow_ratio') || 'No-Shows vs Total'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <NoShowsRatioCard timeRange={timeRange} />
              </CardContent>
            </CardV2>
          </div>
          <div className="lg:col-span-2">
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  {t('stats.meetings_noshow_over_time') || 'No-Shows Over Time'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <NoShowsOverTime timeRange={timeRange} />
              </CardContent>
            </CardV2>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="bookings" className="space-y-6">
        <SectionTitle text={t('stats.bookings') || 'Bookings'} />
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  {t('stats.bookings_total_pending') || 'Total Pending Bookings'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BookingsTotalPendingCard timeRange={timeRange} />
              </CardContent>
            </CardV2>
          </div>
          <div className="lg:col-span-2">
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  {t('stats.bookings_type_distribution') || 'Bookings Type Distribution'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BookingsTypeDistribution timeRange={timeRange} />
              </CardContent>
            </CardV2>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <CardV2>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                {t('stats.bookings_inbound_channels') || 'Bookings inbound channels'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BookingsInboundChannels timeRange={timeRange} />
            </CardContent>
          </CardV2>
          <CardV2>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                {t('stats.bookings_price_ranges') || 'Bookings price ranges'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BookingsPendingPriceRanges timeRange={timeRange} />
            </CardContent>
          </CardV2>
        </div>
      </TabsContent>
    </TabV2>
  );
};

const SalesTotalCard = ({ timeRange }: { timeRange: TimeRange }) => {
  const { t } = useTranslation();
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await SalesInsightsAPI.getSalesSummary({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        if (!mounted) return;
        setTotal(response.data?.totalSales || 0);
      } catch (error) {
        console.error('Error fetching sales total:', error);
        if (!mounted) return;
        setTotal(0);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => { mounted = false };
  }, [timeRange.start, timeRange.end]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-20">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-2xl font-bold text-foreground">{total}</p>
      <p className="text-sm text-muted-foreground">{t('sales.insights.total_sales') || 'Total Sales'}</p>
    </div>
  );
};

const MoneyTotalCard = ({ timeRange }: { timeRange: TimeRange }) => {
  const { t } = useTranslation();
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await SalesInsightsAPI.getSalesSummary({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        if (!mounted) return;
        setTotal(response.data?.totalMoney || 0);
      } catch (error) {
        console.error('Error fetching money total:', error);
        if (!mounted) return;
        setTotal(0);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => { mounted = false };
  }, [timeRange.start, timeRange.end]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-20">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-2xl font-bold text-foreground">
        {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
      <p className="text-sm text-muted-foreground">{t('sales.insights.total_money') || 'Total Money'}</p>
    </div>
  );
};

const SalesContent = ({ timeRange }: { timeRange: TimeRange }) => {
  const { t } = useTranslation();
  const [nestedTab, setNestedTab] = useState<'overview' | 'details'>('overview');

  return (
    <TabV2 value={nestedTab} onValueChange={(v) => setNestedTab(v as any)} className="space-y-4">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="overview" className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          {t('stats.overview') || 'Overview'}
        </TabsTrigger>
        <TabsTrigger value="details" className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          {t('sales.insights.details') || 'Details'}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-6">
        <div className="space-y-6">
          <SectionTitle text={t('sales.insights.summary') || 'Summary'} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Row 1 */}
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  {t('sales.insights.total_sales') || 'Total Sales'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SalesTotalCard timeRange={timeRange} />
              </CardContent>
            </CardV2>
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t('sales.insights.total_money') || 'Total Money'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MoneyTotalCard timeRange={timeRange} />
              </CardContent>
            </CardV2>
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t('sales.insights.avg_sale_price') || 'Avg. sale price'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AverageSalePriceCard timeRange={timeRange} />
              </CardContent>
            </CardV2>
            {/* Row 2 */}
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t('sales.insights.avg_dress_sale_price') || 'Avg. dress sale price'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AverageDressSalePriceCard timeRange={timeRange} />
              </CardContent>
            </CardV2>
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t('sales.insights.deposited_payments') || 'Deposited payments'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DepositedPaymentsCard timeRange={timeRange} />
              </CardContent>
            </CardV2>
            <CardV2>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t('sales.insights.pending_payments') || 'Pending payments'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PendingPaymentsCard timeRange={timeRange} />
              </CardContent>
            </CardV2>
          </div>
          <MoneyInPerPeriod timeRange={timeRange} />
        </div>
      </TabsContent>

      <TabsContent value="details" className="space-y-6">
        <div className="space-y-4">
          <SectionTitle text={t('sales.insights.employee_performance') || 'Employee performance'} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MoneyPerSalesRepresentative timeRange={timeRange} />
            <SalesPerSalesRepresentative timeRange={timeRange} />
          </div>
        </div>
        <div className="space-y-4">
          <SectionTitle text={t('sales.insights.product_performance') || 'Product performance'} />
          <RankingOfSoldItems timeRange={timeRange} />
        </div>
      </TabsContent>
    </TabV2>
  );
};

function UnifiedStatisticsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  
  const initialTab = useMemo((): 'bookings' | 'customers' | 'sales' => {
    if (location.pathname.includes('/insights/sales')) return 'sales';
    if (location.pathname.includes('/insights/customers-crm')) return 'customers';
    return 'bookings';
  }, [location.pathname]);
  
  const [activeTab, setActiveTab] = useState<'bookings' | 'customers' | 'sales'>(initialTab);
  
  // Update tab when route changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthEnd = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [timeRange, setTimeRange] = useState<TimeRange>({ start: toISODate(monthStart), end: toISODate(monthEnd) });

  const onDateChange = (key: 'start' | 'end', value: string) => {
    const date = new Date(value);
    setTimeRange((prev) => ({ ...prev, [key]: toISODate(date) }));
  };

  const startInputValue = useMemo(() => new Date(timeRange.start).toISOString().slice(0, 10), [timeRange.start]);
  const endInputValue = useMemo(() => new Date(timeRange.end).toISOString().slice(0, 10), [timeRange.end]);

  // Customers CRM data
  const [channelsLabels, setChannelsLabels] = useState<string[]>([]);
  const [channelsValues, setChannelsValues] = useState<number[]>([]);
  const [overTimeLabels, setOverTimeLabels] = useState<string[]>([]);
  const [overTimeValues, setOverTimeValues] = useState<number[]>([]);

  // Fetch inbound channels
  useEffect(() => {
    let mounted = true;
    CustomersCrmStatisticsAPI.getInboundChannels({ startTime: timeRange.start, endTime: timeRange.end })
      .then(({ data }) => {
        if (!mounted) return;
        const rawLabels = data.labels || [];
        const translatedLabels = rawLabels.map(label => t(inboundChannelToTranslationKeys(label)));
        setChannelsLabels(translatedLabels);
        setChannelsValues(data.values || []);
      })
      .catch(() => {
        if (!mounted) return;
        setChannelsLabels([]);
        setChannelsValues([]);
      });
    return () => { mounted = false };
  }, [timeRange.start, timeRange.end, t]);

  // Fetch customers over time
  useEffect(() => {
    let mounted = true;
    CustomersCrmStatisticsAPI.getTotalOverTime({ startTime: timeRange.start, endTime: timeRange.end })
      .then(({ data }) => {
        if (!mounted) return;
        setOverTimeLabels(data.labels || []);
        setOverTimeValues(data.values || []);
      })
      .catch(() => {
        if (!mounted) return;
        setOverTimeLabels([]);
        setOverTimeValues([]);
      });
    return () => { mounted = false };
  }, [timeRange.start, timeRange.end]);

  return (
    <PageWrapper>
      <AdminRestricted>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              <h1 className="text-3xl font-bold text-foreground">
                {t('menu.stats') || 'Statistics'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('stats.filters.from') || 'From'}</span>
                <input 
                  type="date" 
                  value={startInputValue} 
                  onChange={(e) => onDateChange('start', e.target.value)} 
                  className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" 
                />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('stats.filters.to') || 'To'}</span>
                <input 
                  type="date" 
                  value={endInputValue} 
                  onChange={(e) => onDateChange('end', e.target.value)} 
                  className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" 
                />
              </div>
            </div>
          </div>

          <TabV2 value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="bookings" className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                {t('stats.meetings_bookings.title') || 'Bookings & Meetings'}
              </TabsTrigger>
              <TabsTrigger value="customers" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                {t('stats.customers_crm') || 'Customers'}
              </TabsTrigger>
              <TabsTrigger value="sales" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                {t('menu.sales') || 'Sales'}
              </TabsTrigger>
            </TabsList>

            {/* Bookings & Meetings Tab */}
            <TabsContent value="bookings" className="space-y-6 max-h-screen overflow-y-auto">
              <BookingsMeetingsContent timeRange={timeRange} />
            </TabsContent>

            {/* Customers Tab */}
            <TabsContent value="customers" className="space-y-6 max-h-screen overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <SectionTitle text={t('stats.customers_total') || 'Customers total'} />
                  <div className="bg-card rounded-lg border border-input p-4">
                    <CustomersCrmTotalCard timeRange={timeRange} />
                  </div>
                </div>
                <div className="space-y-4">
                  <SectionTitle text={t('stats.inbound_channels') || 'Inbound channels'} />
                  {channelsLabels.length ? (
                    <PieChart
                      title={t('stats.inbound_channels') || 'Inbound channels'}
                      labels={channelsLabels}
                      data={channelsValues}
                    />
                  ) : (
                    <NoData message={t('global.empty_results')} />
                  )}
                </div>

                <div className="space-y-4">
                  <SectionTitle text={t('stats.total_over_time') || 'Total over time'} />
                  {overTimeLabels.length ? (
                    <BarChart
                      title={t('stats.total_over_time') || 'Total over time'}
                      xLabels={overTimeLabels}
                      dataSets={[{ label: t('stats.customers') || 'Customers', data: overTimeValues, backgroundColor: Array(overTimeValues.length).fill('rgba(16, 185, 129, 0.6)'), borderColor: Array(overTimeValues.length).fill('rgba(16, 185, 129, 0.6)'), borderWidth: 1 }]}
                    />
                  ) : (
                    <NoData message={t('global.empty_results')} />
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Sales Tab */}
            <TabsContent value="sales" className="space-y-6 max-h-screen overflow-y-auto">
              <SalesContent timeRange={timeRange} />
            </TabsContent>
          </TabV2>
        </div>
      </AdminRestricted>
    </PageWrapper>
  );
}

export default UnifiedStatisticsPage;
